import { makeBetterEnum } from '../utils/betterEnum';
import { GhgScopeDisplayName } from '../constants';
import {
  ALL_FUEL_KINDS,
  CLEAN_RENEWABLE_FUEL_KINDS,
  VEHICLE_FUEL_KINDS,
} from '../batSchemas/constants';
import { getUnitBang, convertUnit } from '../unit';

export const EnergyKind = makeBetterEnum(
  'Electricity',
  'Heat',
  'Steam',
  'Cooling',
  'Co-generation',
  'Tri-generation'
);
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentionally naming the variable the same as the type to mirror enum behavior
export type EnergyKind = keyof typeof EnergyKind;

export const FuelKind = makeBetterEnum(
  'Coal',
  'Oil',
  'Gas',
  'Biomass',
  'Sustainable Biomass',
  'Other'
);
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentionally naming the variable the same as the type to mirror enum behavior
export type FuelKind = keyof typeof FuelKind;

export const FuelKindDisplayNames: Record<FuelKind, string> = {
  Coal: 'Coal',
  Oil: 'Oil',
  Gas: 'Gas',
  Biomass: 'Biomass',
  'Sustainable Biomass': 'Sust. Biomass',
  Other: 'Other',
};

// These should be a subset of the accepted energy units in bat schemas: batSchemas/units.ts
export const EnergyUnit = makeBetterEnum(
  'mwh',
  'kwh',
  'gj',
  'mmbtu',
  'therm',
  'joule',
  'mj'
);

// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentionally naming the variable the same as the type to mirror enum behavior
export type EnergyUnit = keyof typeof EnergyUnit;

const kwhUnit = getUnitBang('kwh');
export interface EnergyData {
  id: string;
  ghgScope: GhgScopeDisplayName;
  aggregationValue?: string | null;

  energyKind: EnergyKind | null;
  fuelKind: FuelKind | null;
  energyContext: Record<string, string>;
  convertedUnit: string;

  isEmissive: boolean;
  tco2e: number;
  locationTco2e: number;
  convertedQuantity: number;
}

// Type including data on the individual energy rows that an energy data was aggregated with
// Keys are row IDs
export type EnergyDataWithAggregateContext = EnergyData & {
  aggregateContext: Record<string, EnergyData>;
};

const COAL_FUEL_KINDS = [
  'Anthracite Coal',
  'Bituminous Coal',
  'Sub-bituminous Coal',
  'Lignite Coal',
  'Mixed (Commercial Sector) Coal',
  'Mixed (Electric Power Sector) Coal',
  'Mixed (Industrial Coking)',
  'Mixed (Industrial Sector)',
  'Coal Coke',
  'Petroleum Coke (Solid)',
] as const;

const OIL_FUEL_KINDS = [
  'Asphalt and Road Oil',
  'Aviation Gasoline',
  'Butylene',
  'Crude Oil',
  'Distillate Fuel Oil No. 1',
  'Distillate Fuel Oil No. 2',
  'Distillate Fuel Oil No. 4',
  'Ethylene',
  'Heavy Gas Oils',
  'Isobutane',
  'Isobutylene',
  'Kerosene',
  'Kerosene-Type Jet Fuel',
  'Lubricants',
  'Motor Gasoline',
  'Naphtha (<401 deg F)',
  'Natural Gasoline',
  'Other Oil (>401 deg F)',
  'Pentanes Plus',
  'Petrochemical Feedstocks',
  'Propylene',
  'Residual Fuel Oil No. 5',
  'Residual Fuel Oil No. 6',
  'Special Naphtha',
  'Unfinished Oils',
  'Used Oil',
  'Diesel Fuel',
] as const;

const GAS_FUEL_KINDS = [
  'Natural Gas',
  'Blast Furnace Gas',
  'Coke Oven Gas',
  'Fuel Gas',
  'Propane Gas',
  'Butane',
  'Ethane',
  'Liquefied Petroleum Gases (LPG)',
  'Propane',
  'Compressed Natural Gas (CNG)',
  'Liquefied Natural Gas (LNG)',
] as const;

const BIOMASS_FUEL_KINDS = [
  'Landfill Gas',
  'Other Biomass Gases',
  'Bagasse',
  'Bamboo',
  'Biofuels and waste',
  'Biodiesel (100%)',
  'Ethanol (100%)',
  'North American Hardwood',
  'North American Softwood',
  'Rendered Animal Fat',
  'Vegetable Oil',
  'Municipal Solid Waste',
  'Agricultural Byproducts',
  'Peat',
  'Solid Byproducts',
  'Straw',
  'Wood and Wood Residuals',
] as const;

const OTHER_FUEL_KINDS = ['Plastics', 'Tires', 'Electricity'] as const;

export const UNKNOWN_FUEL_KINDS = [
  'Steam Heat',
  'District Heat',
  'District Chilled Water',
] as const;

// The following FUEL_KINDS constants need to be a complete mapping of the various
// EPA_*_FUEL_KINDS that might appear in our BATs to one of the CDP fuel definitions.
// We ensure every BAT fuel kind is mapped with the following fuelKindCheck const
// that will fail if something is added in the BAT cave but not also added here.
//
// See https://watershedclimate.slack.com/archives/C03TRKE58PL/p1684531015144999.
const ALL_REPORTING_FUEL_KINDS = [
  ...COAL_FUEL_KINDS,
  ...OIL_FUEL_KINDS,
  ...GAS_FUEL_KINDS,
  ...BIOMASS_FUEL_KINDS,
  ...OTHER_FUEL_KINDS,
  ...UNKNOWN_FUEL_KINDS,
  ...VEHICLE_FUEL_KINDS,
  ...CLEAN_RENEWABLE_FUEL_KINDS,
];
export const fuelKindCheck: ReadonlyArray<
  (typeof ALL_REPORTING_FUEL_KINDS)[number]
> = [...ALL_FUEL_KINDS, ...VEHICLE_FUEL_KINDS];

/** Names of marta columns we use to derive fuelKind and energyKind. */
export const EnergyDataColumns = [
  'convertedUnit',
  'electricityType',
  'fuelCombustionKind',
  'description',
  'categoryId',
  'subcategoryId',
  'ef.efName',
  'bart.businessActivityType',
  'bart.fuelCombustion.fuelKind',
  'bart.fuelCombustion.fuelCombustionReason',
  'bart.nonBuildingElectricityConsumption.electricityConsumptionReason',
  'bart.buildingStationaryCombustion.fuelKind',
  'bart.vehicles.fuelKind',
] as const;

export type EnergyDataMartaRowSelectColumn =
  | (typeof EnergyDataColumns)[number]
  | 'ghgScope'
  | 'ef.kgco2e'
  | 'buildingName'
  | 'locationCountry';

export type EnergyDataMartaRow = Record<
  (typeof EnergyDataColumns)[number],
  string | undefined | null
>;

export function deriveFuelKind(item: EnergyDataMartaRow): FuelKind | null {
  const explicitFuelKind =
    item['bart.fuelCombustion.fuelKind'] ??
    item['bart.buildingStationaryCombustion.fuelKind'] ??
    item['bart.vehicles.fuelKind'];

  const fromBartFuelKind = explicitFuelKind
    ? bartToReportingFuelKind(explicitFuelKind)
    : null;
  if (fromBartFuelKind) {
    return fromBartFuelKind;
  }

  const businessActivityType = item['bart.businessActivityType'];
  if (businessActivityType) {
    if (businessActivityType === 'travel_private_jets') {
      return FuelKind.Oil;
    }
    if (businessActivityType === 'travel_air') {
      return FuelKind.Oil;
    }
  }

  if (item.subcategoryId === 'Natural gas') {
    return FuelKind.Gas;
  }

  // Pipeline v2 output for description maps nicely to our fuel kinds so use description
  // if it happens to match up.
  if (item.description) {
    const cleanDescription = item.description
      .replace(' well-to-tank', '')
      .toLowerCase();
    switch (cleanDescription) {
      case 'natural gas':
        return FuelKind.Gas;
      case 'oil':
        return FuelKind.Oil;
      case 'coal':
        return FuelKind.Coal;
      case 'biofuel':
        return FuelKind.Biomass;
    }
  }

  return null;
}

/**
 * Return the coarse FuelKind from the detailed EPA fuel kind in our bat or null if it could not be mapped.
 */
export function bartToReportingFuelKind(bartFuelKind: string): FuelKind | null {
  const untypedFuelKind = bartFuelKind as any;
  if (OIL_FUEL_KINDS.includes(untypedFuelKind)) {
    return FuelKind.Oil;
  }
  if (GAS_FUEL_KINDS.includes(untypedFuelKind)) {
    return FuelKind.Gas;
  }
  if (COAL_FUEL_KINDS.includes(untypedFuelKind)) {
    return FuelKind.Coal;
  }
  if (BIOMASS_FUEL_KINDS.includes(untypedFuelKind)) {
    return FuelKind.Biomass;
  }
  if (OTHER_FUEL_KINDS.includes(untypedFuelKind)) {
    return FuelKind.Other;
  }
  return null;
}

export function deriveEnergyKind(
  item: EnergyDataMartaRow,
  fuelKind: string | null
): EnergyKind | null {
  if (item['electricityType'] != null) {
    return EnergyKind.Electricity;
  }
  if (item['fuelCombustionKind'] === 'mobile') {
    // Mobile combustion is self-generation of heat.
    return EnergyKind.Heat;
  }
  if (fuelKind === 'Steam Heat' || fuelKind === 'District Heat') {
    return EnergyKind.Heat;
  }
  if (fuelKind === 'District Chilled Water') {
    return EnergyKind.Cooling;
  }

  // Additional rules based on categories.
  const buildingCategoryIds = ['offices', 'buildings', 'retail_buildings'];
  if (
    item.subcategoryId === 'Natural gas' &&
    !!item.categoryId &&
    buildingCategoryIds.includes(item.categoryId)
  ) {
    return EnergyKind.Heat;
  }

  // Pipeline v2 output for description has some clear values for district heat / cooling.
  if (item.description) {
    const cleanDescription = item.description
      .replace(' well-to-tank', '')
      .toLowerCase();
    switch (cleanDescription) {
      case 'district heat':
        return EnergyKind.Heat;
      case 'district chilled water':
      case 'district cooling':
        return EnergyKind.Cooling;
    }
  }

  return null;
}

export function getkWhScalar(unit: EnergyUnit): number {
  return convertUnit(1, kwhUnit, getUnitBang(unit));
}
export const convertFromkWh = (kwh: number, unit: EnergyUnit): number => {
  return convertUnit(kwh, kwhUnit, getUnitBang(unit));
};
export const convertTokWh = (value: number, unit: EnergyUnit): number => {
  return convertUnit(value, getUnitBang(unit), kwhUnit);
};
